import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { removeFromCart, updateQuantity } from '../store/slices/cartSlice';
import { formatNaira } from '../utils/currency';
import { Minus, Plus, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function Cart() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  const handleQuantityChange = (
    id: string | number,
    selectedSize: string | number,
    selectedColor: string,
    quantity: number
  ) => {
    if (quantity < 1) {
      dispatch(removeFromCart({ id, selectedSize, selectedColor }));
    } else {
      dispatch(updateQuantity({ id, selectedSize, selectedColor, quantity }));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h2 className="text-3xl font-bold mb-8">Shopping Cart</h2>
      {cartItems.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-600 text-lg mb-4">Your cart is empty</p>
          <a href="/" className="text-purple-600 hover:text-purple-700 font-medium">
            Continue Shopping
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div key={`${item.id}-${item.selectedSize}-${item.selectedColor?.name}`} 
                  className="flex items-center gap-4 bg-white p-4 rounded-lg shadow"
                >
                  <img
                    src={item.selectedColor?.image || item.image}
                    alt={`${item.name} in ${item.selectedColor?.name || 'default color'}`}
                    className="w-24 h-24 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{item.name}</h3>
                    <p className="text-gray-600">{formatNaira(item.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm text-gray-500">
                        Size: {typeof item.selectedSize === 'number' ? `EU ${item.selectedSize}` : item.selectedSize}
                      </span>
                      {item.selectedColor && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span className="text-sm text-gray-500">
                            Color: {item.selectedColor.name}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleQuantityChange(
                        item.id,
                        item.selectedSize,
                        item.selectedColor?.name || '',
                        item.quantity - 1
                      )}
                      className="p-1 rounded-full hover:bg-gray-100"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => handleQuantityChange(
                        item.id,
                        item.selectedSize,
                        item.selectedColor?.name || '',
                        item.quantity + 1
                      )}
                      className="p-1 rounded-full hover:bg-gray-100"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => dispatch(removeFromCart({
                      id: item.id,
                      selectedSize: item.selectedSize,
                      selectedColor: item.selectedColor?.name || ''
                    }))}
                    className="p-2 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Order Summary</h3>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatNaira(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-gray-600 italic">Depends on location</span>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold">Total</span>
                  <div className="text-right">
                    <span className="text-lg font-semibold">{formatNaira(subtotal)}</span>
                    <p className="text-sm text-gray-500">(excluding shipping)</p>
                  </div>
                </div>
              </div>
              <button 
                onClick={() => navigate('/checkout')}
                className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Proceed to Checkout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;